import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Zap, Mail, Lock, User, Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/auth";
import { PublicNav, PublicFooter } from "../components/PublicLayout";

const inputWrap = "flex items-center gap-2 bg-[var(--surface2)] border border-[var(--bd)] rounded-md px-3 focus-within:border-[var(--accent)] transition-colors";
const inputCls = "flex-1 bg-transparent py-3 text-sm focus:outline-none";

export default function Register() {
  const { register, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agree, setAgree] = useState(false);
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  const handleGoogle = async () => {
    setGoogleBusy(true);
    setErr("");
    try {
      await loginWithGoogle();
      nav("/app");
    } catch (e2) {
      if (e2.code !== "auth/popup-closed-by-user") {
        setErr(e2.message);
      }
    } finally { setGoogleBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!agree) { setErr("Please accept the Terms & Privacy Policy to continue."); return; }
    setBusy(true);
    try {
      await register(name, email, password);
      nav("/app");
    } catch (e2) {
      const codes = {
        "auth/email-already-in-use": "An account with this email already exists.",
        "auth/invalid-email": "Please enter a valid email address.",
        "auth/weak-password": "Password is too weak. Use at least 6 characters.",
        "auth/operation-not-allowed": "Email/password sign-up is not enabled. Contact admin.",
      };
      setErr(codes[e2.code] || formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[100dvh] flex flex-col bg-[var(--bg)] text-[var(--t1)]">
      <PublicNav />
      <div className="flex-1 grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden border-r border-[var(--bd)] order-1">
        <img src="/assets/hw-panel-ezremove.png" alt="EnergyLens hardware" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10">
          <div className="font-heading font-black text-3xl text-white">Join the platform</div>
          <p className="text-white/70 mt-2">Per-unit electricity submetering, in your browser.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 order-2">
        <div className="w-full max-w-sm">
          <h1 className="font-heading font-black text-3xl tracking-tight">Create your account</h1>
          <p className="text-[var(--t2)] text-sm mt-1">Start monitoring electricity use in real time.</p>

          {err && <div className="mt-5 px-4 py-3 rounded-md bg-[var(--danger)]/10 border border-[var(--danger)]/30 text-[var(--danger)] text-sm" data-testid="register-error">{err}</div>}

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
            <div className={inputWrap}>
              <User size={16} className="text-[var(--t2)]" />
              <input data-testid="register-name" required placeholder="Full name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className={inputWrap}>
              <Mail size={16} className="text-[var(--t2)]" />
              <input data-testid="register-email" type="email" required placeholder="Email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className={inputWrap}>
              <Lock size={16} className="text-[var(--t2)]" />
              <input data-testid="register-password" type={show ? "text" : "password"} required minLength={6} placeholder="Password (min 6 chars)" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} />
              <button type="button" onClick={() => setShow((s) => !s)} className="text-[var(--t2)]">{show ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </div>
            <label className="flex items-start gap-2 text-xs text-[var(--t2)] cursor-pointer">
              <input data-testid="register-agree" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 accent-[var(--accent)]" />
              <span>I agree to the <Link to="/terms" className="text-[var(--accent)] underline">Terms & Conditions</Link> and <Link to="/privacy" className="text-[var(--accent)] underline">Privacy Policy</Link>.</span>
            </label>
            <button type="submit" disabled={busy} data-testid="register-submit" className="w-full py-3 rounded-md bg-[var(--accent)] text-black font-semibold flex items-center justify-center gap-2 active:scale-[0.99] transition-transform disabled:opacity-60">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <>Create Account <ArrowRight size={18} /></>}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[var(--bd)]" />
            <span className="text-xs text-[var(--t2)]">or</span>
            <div className="flex-1 h-px bg-[var(--bd)]" />
          </div>

          <button
            onClick={handleGoogle}
            disabled={googleBusy}
            data-testid="register-google"
            className="w-full py-3 rounded-md border border-[var(--bd)] bg-[var(--surface2)] text-sm font-medium flex items-center justify-center gap-3 hover:border-[var(--bd-strong)] active:scale-[0.99] transition-all disabled:opacity-60"
          >
            {googleBusy ? <Loader2 size={18} className="animate-spin" /> : (
              <>
                <svg width="18" height="18" viewBox="0 0 48 48" fill="none">
                  <path d="M47.532 24.553c0-1.632-.145-3.2-.415-4.695H24.48v8.878h12.987c-.56 3.02-2.26 5.578-4.814 7.296v6.063h7.793c4.56-4.2 7.086-10.393 7.086-17.542z" fill="#4285F4"/>
                  <path d="M24.48 48c6.516 0 11.987-2.163 15.982-5.865l-7.793-6.063c-2.162 1.45-4.928 2.308-8.19 2.308-6.3 0-11.636-4.253-13.546-9.97H2.9v6.254C6.878 42.933 15.096 48 24.48 48z" fill="#34A853"/>
                  <path d="M10.934 28.41A14.41 14.41 0 0 1 10.01 24c0-1.53.264-3.015.924-4.41v-6.254H2.9A23.946 23.946 0 0 0 .48 24c0 3.86.93 7.515 2.42 10.664l8.034-6.254z" fill="#FBBC05"/>
                  <path d="M24.48 9.622c3.554 0 6.74 1.222 9.254 3.62l6.937-6.937C36.46 2.392 30.993 0 24.48 0 15.096 0 6.878 5.067 2.9 13.336l8.034 6.254c1.91-5.717 7.246-9.968 13.546-9.968z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </>
            )}
          </button>

          <p className="text-sm text-[var(--t2)] mt-6 text-center">
            Already have an account? <Link to="/login" className="text-[var(--accent)] font-medium" data-testid="link-login">Sign in</Link>
          </p>
        </div>
      </div>

      </div>
      <PublicFooter />
    </div>
  );
}
